import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["listId", "flowId"],
  _excluded2 = ["actions", "associatedLists", "createMetadata", "updateMetadata", "classicEnrollmentSettings", "enrollmentSchedule", "timeWindow", "firstActionId", "scheduledDisableAt"];
import { List, Set as ImmutableSet } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
import * as CRMObjectTypes from 'automation-ui-references/object-types/CRMObjectTypes';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import Action from 'automation-ui-action-features/records/Action';
import FlowList from 'automation-ui-canvas/flow/records/properties/FlowList';
import UpdateMetadata from 'automation-ui-canvas/flow/records/properties/UpdateMetadata';
import ClassicEnrollmentSettings from 'automation-ui-canvas/flow/records/properties/ClassicEnrollmentSettings';
import EnrollmentSchedule from 'automation-ui-enrollment/records/EnrollmentSchedule';
import * as ConnectorEdgeTypes from 'automation-ui-action-features/constants/ConnectorEdgeTypes';
import createEntityMap from 'automation-ui-canvas/flow/utils/createEntityMap';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { createReverseEdgeMap, getId as getActionId, getPreviousActionId, getMasterPreviousEdge, getNewConnection, DEFAULT_BRANCH_INDEX } from 'automation-ui-action-features/records/recordsUtils';
import { getEnrollmentFilters, getWorkflowId, getClassicEnrollmentSettings, getClassicGoalCriteria, getClassicSuppressionListIds, getClassicUnenrollmentSetting, getClassicEventAnchor, getClassicListening, getClassicCanEnrollFromSalesforce, getTemplateMetadata, getInternalStartingListId, getDescription, getSegmentCriteria, getSuppressionFilters, getReenrollmentFilters, findAllAncestorsAndDecendantsOfAction, getAssociatedListByType, getTriggers, hasBranchActions as hasBranchActionsUtil } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as FlowTypes from 'automation-ui-canvas/flow/constants/FlowTypes';
import TimeWindowMetadata from 'automation-ui-canvas/flow/records/properties/Timing/TimeWindowMetadata';
export const ENROLLMENT_EDGE = 'enrollment-edge-0';

// The FE representation of a flow.

/**
 * Full BE representation of an Automation Platform workflow.
 * This Record is passed unchanged to the BE when interacting
 * with AP services. While most of it is metadata, there are
 * several important graph modification methods that are used
 * for connectivity features.
 *
 * A Flow will contain Action records which in turn contain
 * Connection records. These records can all used to create
 * a DirectedNodes record, an abstract representation of a
 * DAG used in rendering of the canvas.
 *
 * It's important to note that all graph modifications are
 * done at this level rather than at the DirectedNodes level.
 * This is because the DAG rendering logic is meant to be
 * exclusively a representation of a DAG and not opinionated
 * in terms of any connectivity changes.
 *
 * The Action and Connection records, as well as the utility
 * file in the records' folder, contain a majority of the
 * logic used for graph modifications.
 */
export default class Flow extends createImmutableObjectClass({
  portalId: null,
  flowId: null,
  firstActionId: null,
  flowEventFilters: null,
  actions: null,
  objectTypeId: null,
  isEnabled: null,
  uuid: null,
  name: null,
  associatedLists: null,
  createMetadata: null,
  updateMetadata: null,
  nextAvailableActionId: null,
  shouldReenroll: null,
  scheduledDisableAt: null,
  version: null,
  timeWindow: null,
  classicEnrollmentSettings: null,
  enrollmentSchedule: null,
  isClassicWorkflow: false,
  enrollmentTrigger: 'LIST',
  enrollmentCriteria: null,
  enrollmentDataset: null,
  flowType: FlowTypes.WORKFLOW,
  flowObjectType: 'UNKNOWN',
  enrolledType: null
}, {
  fromJSON: json => {
    const {
      actions,
      associatedLists,
      createMetadata,
      updateMetadata,
      classicEnrollmentSettings,
      enrollmentSchedule,
      objectTypeId,
      flowId,
      version,
      timeWindow,
      flowType
    } = json;
    const actionJsMap = Object.values(actions || {});
    const actionMap = createEntityMap(Action, getActionId, actionJsMap);
    /*
     * Anything stored in apollos cache is deeply frozen.
     * Call hashCode to set the cache property before we load it into
     * data-fetching-client's cache so it doesn't try to write and throw
     * an exception
     */
    actionMap.hashCode();

    // Enforce that only contact-based workflows may use classic enrollment and
    // other hybrid features
    let isClassicWorkflow = json.isClassicWorkflow || false;
    if (isClassicWorkflow && objectTypeId !== CRMObjectTypes.CONTACT_ID) {
      isClassicWorkflow = false;
      reportError({
        error: new Error('Non-contact flow metadata is flagged as classic workflow'),
        tags: {
          flowObjectTypeId: objectTypeId,
          flowId: flowId,
          flowVersion: version
        }
      });
    }
    return Object.assign({}, json, {
      actions: actionMap,
      associatedLists: List((associatedLists || []).map(flowList => new FlowList(flowList))),
      createMetadata: createMetadata ? new UpdateMetadata(createMetadata) : null,
      updateMetadata: updateMetadata ? new UpdateMetadata(updateMetadata) : null,
      classicEnrollmentSettings: isClassicWorkflow && classicEnrollmentSettings ? new ClassicEnrollmentSettings(classicEnrollmentSettings) : null,
      enrollmentSchedule: enrollmentSchedule ? new EnrollmentSchedule(enrollmentSchedule) : null,
      isClassicWorkflow,
      timeWindow: timeWindow ? new TimeWindowMetadata(timeWindow) : null,
      stagedVersion: null,
      flowOptimizations: null,
      flowType: flowType
    });
  },
  toJSON: _ref => {
    let {
        actions,
        associatedLists,
        createMetadata,
        updateMetadata,
        classicEnrollmentSettings,
        enrollmentSchedule,
        timeWindow,
        firstActionId,
        scheduledDisableAt
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
    return Object.assign({
      actions: actions && actions.toJS(),
      associatedLists: associatedLists.toJS(),
      createMetadata: createMetadata && createMetadata.toJS(),
      updateMetadata: updateMetadata && updateMetadata.toJS(),
      classicEnrollmentSettings: classicEnrollmentSettings && classicEnrollmentSettings.toJS(),
      enrollmentSchedule: enrollmentSchedule && enrollmentSchedule.toEnrollmentScheduleInterface(),
      timeWindow: timeWindow && timeWindow.toJS(),
      firstActionId: firstActionId,
      scheduledDisableAt: scheduledDisableAt
    }, rest);
  }
}) {
  /**
   * Converts this instance of the Flow object to conform to a more stripped-down
   * HybridFlow interface, which allows the flow object to be used in a read-only
   * fashion in packages that can't directly depend on automation-ui-flow for one
   * reason or another
   */
  toHybridFlowInterface() {
    var _this$getCreateMetada, _this$getEnrollmentSc, _this$getUpdateMetada;
    return {
      actions: this.getActions().toJS(),
      associatedLists: this.associatedLists.toArray().map(list => list.toAssociatedListInterface()),
      classicEnrollmentSettings: this.getClassicEnrollmentSettings(),
      createMetadata: (_this$getCreateMetada = this.getCreateMetadata()) === null || _this$getCreateMetada === void 0 ? void 0 : _this$getCreateMetada.toJSON(),
      enrolledType: this.enrolledType,
      enrollmentCriteria: this.enrollmentCriteria,
      enrollmentSchedule: (_this$getEnrollmentSc = this.getEnrollmentSchedule()) === null || _this$getEnrollmentSc === void 0 ? void 0 : _this$getEnrollmentSc.toEnrollmentScheduleInterface(),
      enrollmentTrigger: this.enrollmentTrigger,
      firstActionId: this.getFirstActionId(),
      flowEventFilters: this.flowEventFilters,
      flowId: this.getFlowId(),
      // This field is deprecated and we should be using `objectTypeId` instead. It's still required in the interface though so we need to provide it here
      flowObjectType: 'UNKNOWN',
      flowType: this.flowType,
      isClassicWorkflow: this.getIsClassicWorkflow(),
      isEnabled: this.isEnabled,
      name: this.getName(),
      nextAvailableActionId: this.getNextAvailableActionId(),
      objectTypeId: this.getObjectTypeId(),
      portalId: this.portalId,
      scheduledDisableAt: this.getScheduledDisableAt(),
      shouldReenroll: this.getShouldReenroll(),
      // Unused in HybridFlow at the moment
      timeWindow: null,
      updateMetadata: (_this$getUpdateMetada = this.getUpdateMetadata()) === null || _this$getUpdateMetada === void 0 ? void 0 : _this$getUpdateMetada.toJSON(),
      uuid: this.uuid,
      version: this.getVersion()
    };
  }
  getActions() {
    return this.actions;
  }
  getFlowId() {
    return this.flowId;
  }
  getName() {
    return this.name;
  }
  getCreateMetadata() {
    return this.createMetadata;
  }
  getTemplateMetadata() {
    return getTemplateMetadata(this);
  }
  getTemplateId() {
    return getIn(['templateId'], getTemplateMetadata(this)) || null;
  }
  getNextAvailableActionId() {
    return this.nextAvailableActionId;
  }
  getFirstActionId() {
    return this.firstActionId;
  }
  getObjectTypeId() {
    return this.objectTypeId;
  }
  getIsClassicWorkflow() {
    return this.isClassicWorkflow;
  }
  getEnrollmentFilters() {
    return getEnrollmentFilters(this);
  }
  getSegmentCriteria() {
    return getSegmentCriteria(this);
  }
  getSuppressionFilters() {
    return getSuppressionFilters(this);
  }
  getReenrollmentFilters() {
    return getReenrollmentFilters(this);
  }
  getUpdateMetadata() {
    return this.updateMetadata;
  }
  getUpdatedUserId() {
    return getIn(['updatedBy', 'userId'], this.getUpdateMetadata()) || null;
  }
  getActionsAsList() {
    return this.getActions().valueSeq().toArray();
  }
  getVersion() {
    return this.version;
  }
  getIsEnabled() {
    return this.isEnabled;
  }
  getTimeWindow() {
    return this.timeWindow;
  }
  getScheduledDisableAt() {
    return this.scheduledDisableAt;
  }
  getShouldReenroll() {
    return this.shouldReenroll;
  }
  getEnrollmentSchedule() {
    return this.enrollmentSchedule;
  }
  getTriggers() {
    return getTriggers(this);
  }
  getAssociatedListsAsNew() {
    const associatedLists = this.associatedLists;
    if (!associatedLists.size) {
      return List();
    }
    return List(associatedLists.map(flowList => {
      const _flowList$toJS = flowList.toJS(),
        rest = _objectWithoutPropertiesLoose(_flowList$toJS, _excluded);
      return new FlowList(rest);
    }));
  }
  getActionById(id) {
    return this.getActions().get(id);
  }
  getActionByParentEdgeId(edgeId) {
    if (edgeId === ENROLLMENT_EDGE) {
      return this.getActionById(this.getFirstActionId());
    } else {
      const previousAction = this.getActionById(getPreviousActionId(edgeId));
      const actionId = previousAction && previousAction.getNextActionIdByEdgeId(edgeId);
      return actionId && this.getActionById(actionId);
    }
  }
  isActionSet() {
    return this.flowType === FlowTypes.ACTION_SET;
  }

  /****************************************************************/
  /*                 CLASSIC WORKFLOW ACCESSORS                   */
  /****************************************************************/

  getWorkflowId() {
    return getWorkflowId(this);
  }
  getClassicEnrollmentSettings() {
    return getClassicEnrollmentSettings(this);
  }
  getClassicGoalCriteria() {
    return getClassicGoalCriteria(this);
  }
  getClassicSuppressionListIds() {
    return getClassicSuppressionListIds(this);
  }
  getClassicUnenrollmentSetting() {
    return getClassicUnenrollmentSetting(this);
  }
  getClassicListening() {
    return getClassicListening(this);
  }
  getClassicCanEnrollFromSalesforce() {
    return getClassicCanEnrollFromSalesforce(this);
  }
  getClassicEventAnchor() {
    return getClassicEventAnchor(this);
  }
  getInternalStartingListId() {
    return getInternalStartingListId(this);
  }
  getDescription() {
    return getDescription(this);
  }
  getAssociatedListByType(listType) {
    return getAssociatedListByType(listType)(this);
  }

  /**
   * Certain fields and data on this record are not
   * recognized by the BE so we can use this function
   * to clean up any UI specific logic.
   */
  getSanitizedFlow() {
    const specialNewActionId = SpecialActionIds.NEW_ACTION;
    const newFlow = this.removeAction(specialNewActionId);
    return newFlow;
  }
  setName(name) {
    return this.set('name', name);
  }
  setActions(actions) {
    return this.set('actions', actions);
  }
  setVersion(newVersion) {
    return this.set('version', newVersion);
  }
  setCollapsedBranchPointers(actionId, nextActionId) {
    const flowOptimizations = this.flowOptimizations || {};
    const collapsedBranchPointers = flowOptimizations.collapsedBranchPointers || {};
    return this.set('flowOptimizations', Object.assign({}, flowOptimizations, {
      collapsedBranchPointers: Object.assign({}, collapsedBranchPointers, {
        [actionId]: nextActionId
      })
    }));
  }
  setFirstActionId(firstActionId) {
    return this.set('firstActionId', firstActionId);
  }
  setScheduledDisableAt(scheduledDisableAt) {
    return this.set('scheduledDisableAt', scheduledDisableAt);
  }
  setClassicEnrollmentSettings(classicEnrollmentSettings) {
    return this.set('classicEnrollmentSettings', classicEnrollmentSettings);
  }
  setTimeWindowMetadata(timeWindowMetadata) {
    return this.set('timeWindow', timeWindowMetadata);
  }
  hasEnrollmentSchedule() {
    /* Currently, we check for a schedule just based on the presence of the `enrollmentSchedule` property */
    return !!this.getEnrollmentSchedule();
  }
  hasBranchActions() {
    const actions = this.getActions();
    return hasBranchActionsUtil(actions);
  }
  hasActionTypeId(actionTypeId) {
    const actions = this.getActions();
    return actions.some(action => action.getActionTypeId() === actionTypeId);
  }
  getNumActionsOfType(actionType) {
    const actions = this.getActions();
    return actions.count(action => action.getActionType() === actionType);
  }
  replaceAction(newAction) {
    const actions = this.getActions();
    const actionId = newAction.getActionId();
    const updatedActions = actions.set(actionId, newAction);
    return this.setActions(updatedActions);
  }

  /**
   * Starting point for adding new actions to the graph. This
   * complex method will begin by creating a new connection
   * based on the `edgeId` and new `action` to be added. In
   * the case of branching actions, extra logic is needed to
   * determine special logic like downstream action placement.
   *
   * Always returns a new Flow record with the updated actions.
   */
  insertAction({
    action,
    edgeId,
    branches,
    branchIndexToSetDownstreamActionsIn,
    // assumes is branch
    deleteDownstreamActions,
    insertDownstreamActions,
    defaultBranchName,
    actionId,
    rootLoopNodeId
  }) {
    const actions = this.getActions();
    const newActionId = actionId || this.getNextAvailableActionId();
    const previousActionId = getPreviousActionId(edgeId) || -1;
    const previousAction = this.getActionById(previousActionId);
    let newAction = action.setActionId(newActionId);

    // NOTE if the edge identified by edgeId is not a leaf edge (i.e. has no
    // following action), enabling insertDownstreamActions will cause any
    // actions following that edge to become orphaned
    if (!insertDownstreamActions) {
      const startingActionId = this.getFirstActionId();

      // this will append the inserted action's existing descendents even if the
      // deleteDownstreamActions argument is set to true
      let newConnection = getNewConnection({
        action: newAction,
        previousAction,
        startingActionId,
        branches,
        edgeId,
        branchIndexToSetDownstreamActionsIn,
        defaultBranchName
      });
      if (newConnection && action.isBranch()) {
        newConnection = newConnection.sanitizeBranchConnection(rootLoopNodeId);
      }
      newAction = newAction.setConnection(newConnection);
    }
    let updatedActions = actions.set(newActionId, newAction);
    let newFlow = this.setActions(updatedActions);
    if (!previousAction) {
      newFlow = newFlow.setActions(updatedActions).set('firstActionId', newActionId);
    } else {
      const updatedPreviousAction = previousAction.updateEdgeInConnection(edgeId, newActionId);
      updatedActions = updatedActions.set(previousAction.getActionId(), updatedPreviousAction);
      newFlow = newFlow.setActions(updatedActions);
    }

    // this step is used to clean up actions set downstream
    // if the flag is set to true
    if (deleteDownstreamActions) {
      const actionConnection = newAction.getConnection();
      const actionToDeleteConnection = actionConnection.getConnectorByBranchIndex(branchIndexToSetDownstreamActionsIn || 0);
      const actionToDeleteId = actionToDeleteConnection.getNextActionId();

      // Don't delete the next action if it follows a GOTO edge
      if (!actionToDeleteConnection.isGoToEdge()) {
        newFlow = newFlow.removeAction(actionToDeleteId, true);
      } else {
        const edgeIdToRemove = `${newActionId}-edge-${branchIndexToSetDownstreamActionsIn + 2}`;
        newAction = newAction.removeEdgeFromConnection(edgeIdToRemove);
        updatedActions = updatedActions.set(newActionId, newAction);
        newFlow = newFlow.setActions(updatedActions);
      }
    }
    return newFlow;
  }

  /**
   * When moving or removing actions, we must ensure any parent
   * node is properly accounted for. This method will cleanup
   * all orphaned parent nodes by using the `nextConnection` as
   * a way to figure out what this new connectivity should be.
   *
   * Note that when the we're dealing with the first action of
   * the graph, we must also update the flow's first action id.
   */
  reconnectParentActions(previousActionEdges, nextConnector, actionId__, flow) {
    const nextActionId = nextConnector && nextConnector.getNextActionId();
    const nextActionEdgeType = nextConnector && nextConnector.getEdgeType();
    if (!previousActionEdges.length) {
      const firstActionId = nextConnector && nextConnector.isGoToEdge() ? null : nextActionId;
      return flow.set('firstActionId', firstActionId);
    }
    let newFlow = flow;
    let newActions = newFlow.getActions();
    const nextActionExists = !!newFlow.getActionById(nextActionId);
    previousActionEdges.forEach(previousActionEdge => {
      const previousActionId = getPreviousActionId(previousActionEdge.edgeId);
      const previousActionEdgeEdgeType = previousActionEdge.edgeType;
      const previousAction = newActions.get(previousActionId);
      const actualNextActionId = nextActionExists ? nextActionId : null;
      // if either the previous or next edge is a GOTO, the
      // new connection must be a GOTO
      const actualNextActionEdgeIsGoTo = nextActionExists ? [previousActionEdgeEdgeType, nextActionEdgeType].includes(ConnectorEdgeTypes.GOTO) : false;
      newActions = newActions.set(previousActionId, previousAction.updateEdgeInConnection(previousActionEdge.edgeId, actualNextActionId, actualNextActionEdgeIsGoTo));
      newFlow = newFlow.setActions(newActions);
    });
    return newFlow;
  }

  /**
   * Starting point for deleting an action from the graph. This
   * method will also optionally delete downstream actions or keep
   * one set of actions when deleting branching actions. There
   * is also reconnection logic that will ensure the parent node
   * is reconnected to whichever node comes after the action to be
   * deleted.
   *
   * @param {*} actionId
   * @param {bool} removeAllChildren optional; defaults to false
   * @param {int} branchIndexToKeep optional; only used for
   * deleting list branches
   * @return the new flow with the deleted action/s
   */
  removeAction(actionId, removeAllChildren = false, branchIndexToKeep) {
    const actions = this.getActions();
    const action = this.getActionById(actionId);
    if (!action) {
      return this;
    }
    const childToParentEdges = createReverseEdgeMap(actions);
    const previousActionEdges = childToParentEdges[actionId] || [];
    const masterPreviousEdge = getMasterPreviousEdge(actionId, childToParentEdges) || {};
    const newActions = this.removeActionAndItsChildren(actionId, masterPreviousEdge.edgeId, removeAllChildren, childToParentEdges, branchIndexToKeep);
    let newFlow = this.setActions(newActions);

    // Making an assumption that as long as removeAllChildren is false the moved
    // action will not be a branch action
    const nextConnector = removeAllChildren ? null : action.findNextConnector(branchIndexToKeep);
    newFlow = this.reconnectParentActions(previousActionEdges, nextConnector, actionId, newFlow);
    return newFlow;
  }
  getEnrollmentEdge() {
    return {
      edgeId: ENROLLMENT_EDGE,
      edgeType: ConnectorEdgeTypes.STANDARD
    };
  }

  /**
   * Insert a cloned action and optionally its descendants into the flow.  These
   * actions must first be hydrated using hydrateAction() or hydrateAllActions()
   * and then have their identifiers updated using fetchFixedDataPassingReferences().
   * If includeChildren is false, it is assumed that actions contains only one
   * action and its connection should be cleared.  "root action" should point to
   * the action being inserted at the given edge.
   */
  cloneActions({
    actions,
    rootActionId,
    edgeId,
    branchIndexToSetDownstreamActionsIn,
    deleteDownstreamActions,
    includeChildren
  }) {
    let newActions = this.getActions();
    actions.forEach(action => {
      newActions = newActions.set(action.actionId, action);
    });
    let newFlow = this.setActions(newActions);
    newFlow = includeChildren ? newFlow._cloneAllActions(rootActionId, edgeId) : newFlow._cloneSingleAction(rootActionId, edgeId, branchIndexToSetDownstreamActionsIn, deleteDownstreamActions);
    return newFlow;
  }

  /**
   * Internal function implementing the includeChildren == false variant of
   * cloneActions()
   */
  _cloneSingleAction(rootActionId, edgeId, branchIndexToSetDownstreamActionsIn, deleteDownstreamActions) {
    let clonedAction = this.getActionById(rootActionId);
    const connection = clonedAction.getConnection();
    if (connection) {
      clonedAction = clonedAction.setConnection(connection.updateConnectors(() => null));
    }
    if (connection && connection.isBranchConnection()) {
      const clonedActionConnection = clonedAction.getConnection();
      const branches = clonedActionConnection.getBranches();
      const defaultBranchName = clonedActionConnection.getDefaultBranchName();
      return this.insertAction({
        action: clonedAction,
        edgeId,
        actionId: rootActionId,
        branchIndexToSetDownstreamActionsIn,
        branches,
        defaultBranchName,
        deleteDownstreamActions
      });
    } else {
      return this.insertAction({
        action: clonedAction,
        edgeId,
        actionId: rootActionId
      });
    }
  }

  /**
   * Internal function implementing the includeChildren == true variant of
   * cloneActions()
   */
  _cloneAllActions(rootActionId, edgeId) {
    const rootAction = this.getActionById(rootActionId);
    return this.insertAction({
      action: rootAction,
      edgeId,
      actionId: rootActionId,
      insertDownstreamActions: true
    });
  }

  /**
   * Move a single action by changing its non-goto parent connection,
   * its child connection, and the connection of the new parent.
   * Goto edges are persisted, which may cause a cycle.
   */
  moveAction({
    actionId,
    edgeId,
    moveAllChildren
  }) {
    const action = this.getActionById(actionId);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let newFlow = this;
    const replacementConnection = moveAllChildren ? null : action.getConnection();
    const parentEdges = newFlow.getParentEdges({
      actionId,
      includeGoToEdges: false,
      excludeStandardEdges: false,
      includeEnrollmentEdge: false
    });

    // disconnect the action from its parent
    newFlow = newFlow.reconnectParentActions(parentEdges, replacementConnection, actionId, newFlow);

    // reconnect the action at the new edge
    newFlow = newFlow.insertAction({
      action,
      edgeId,
      actionId,
      insertDownstreamActions: moveAllChildren
    });
    return newFlow;
  }

  /**
   * Determines whether a given action is part of a cycle in the flow
   * @param {number} actionId to start with when looking for a cycle
   */
  hasCycle(actionId) {
    let foundCycle = false;
    let visitedActionIds = ImmutableSet();
    let toExplore = [actionId];
    while (toExplore.length > 0 && !foundCycle) {
      const currentActionId = toExplore.shift();
      if (visitedActionIds.has(currentActionId)) {
        continue;
      }
      visitedActionIds = visitedActionIds.add(currentActionId);
      const childIds = this.findChildIds(currentActionId, true);
      if (childIds.includes(actionId)) {
        foundCycle = true;
      } else {
        toExplore = toExplore.concat(childIds);
      }
    }
    return foundCycle;
  }
  getParentEdges({
    actionId,
    includeGoToEdges = true,
    excludeStandardEdges = false,
    includeEnrollmentEdge = true
  }) {
    const actions = this.getActions();
    let childToParentEdges = createReverseEdgeMap(actions);
    if (includeEnrollmentEdge) {
      const enrollmentEdge = this.getEnrollmentEdge();
      const firstActionId = this.getFirstActionId();
      const firstActionParents = childToParentEdges[firstActionId] || [];
      childToParentEdges = Object.assign({}, childToParentEdges, {
        [firstActionId]: firstActionParents.concat([enrollmentEdge])
      });
    }
    let parentEdges = childToParentEdges[actionId] || [];
    if (!includeGoToEdges) {
      parentEdges = parentEdges.filter(edge => edge.edgeType !== ConnectorEdgeTypes.GOTO);
    }
    if (excludeStandardEdges) {
      parentEdges = parentEdges.filter(edge => edge.edgeType !== ConnectorEdgeTypes.STANDARD);
    }
    return parentEdges;
  }

  /**
   * Given an actionId, get the ids of its parents
   */
  findParentIds({
    actionId,
    includeGoToEdges = true,
    excludeStandardEdges = false
  }) {
    const parentEdges = this.getParentEdges({
      actionId,
      includeGoToEdges,
      excludeStandardEdges
    });
    const parentIds = parentEdges.map(edge => Number(getPreviousActionId(edge.edgeId))).filter((cur, index, array) => array.indexOf(cur) === index);
    return parentIds;
  }

  /**
   * Given an actionId, return an array containing the ids of its children.
   * Go to edges that point back to a loop root node are
   * considered terminating conditions. That is, a loop action
   * will not be returned as a child if the edge is a Go To from within the same loop context.
   */
  findChildIds(actionId, includeGoTo = false) {
    const action = this.getActionById(actionId);
    const connection = action.getConnection();
    if (!connection) {
      return [];
    }
    const childIds = connection.getConnectors().map(connector => {
      if (!connector) {
        return null;
      } else if (!includeGoTo || connector.isGoToEdge()) {
        const nextActionId = connector.getNextActionId();
        const nextAction = nextActionId && this.getActionById(nextActionId);
        return nextAction && !nextAction.isLoopBranch() ? nextActionId : null;
      } else if (includeGoTo || !connector.isGoToEdge()) {
        return connector.getNextActionId();
      }
      return null;
    }).filter(childId => childId !== null);
    return childIds;
  }

  /**
   * Given an `edgeId`, it checks whether this Action
   * would lead to a "finish line".
   *
   * Since the NEW_ACTION node is a real node,
   * allow ignoring this as a possible next action
   * and look at the following node.
   */
  isLeafNodeEdge(edgeId, ignoreNewAction = true) {
    const previousActionId = Number(getPreviousActionId(edgeId));
    let nextActionId;
    if (edgeId === ENROLLMENT_EDGE) {
      nextActionId = this.firstActionId;
    } else {
      const previousAction = this.getActionById(previousActionId);
      nextActionId = previousAction === null || previousAction === void 0 ? void 0 : previousAction.getNextActionIdByEdgeId(edgeId);
    }
    if (nextActionId === SpecialActionIds.NEW_ACTION && ignoreNewAction) {
      const newAction = this.getActionById(SpecialActionIds.NEW_ACTION);
      nextActionId = newAction.getNextActionIdByEdgeId(edgeId);
    }
    return !nextActionId;
  }
  removeGoTosCausingCycles(actionId, validGotoActionIds) {
    let updatedActions = this.getActions();
    const toExplore = [actionId];
    while (toExplore.length > 0) {
      const currentActionId = toExplore.shift();
      const action = this.getActionById(currentActionId);
      let connection = action.getConnection();
      if (!connection) {
        continue;
      }
      connection = connection.updateConnectors(connector => {
        if (connector === null) {
          return null;
        }
        const nextActionId = connector.getNextActionId();
        const nextAction = nextActionId && this.getActionById(nextActionId);
        const nextActionIsLoop = nextAction && nextAction.isLoopBranch();
        if (!nextActionIsLoop && connector.isGoToEdge() && !validGotoActionIds.includes(nextActionId)) {
          return null;
        } else if (!connector.isGoToEdge() && nextActionId) {
          toExplore.push(nextActionId);
        }
        return connector;
      });
      updatedActions = updatedActions.set(currentActionId, action.setConnection(connection));
    }
    return this.setActions(updatedActions);
  }

  /**
   * Internal function used to remove an action and also
   * its children (when needed). It does not reconnect
   * edges from the parent action. It should not be used
   * directly when attempting to delete an action.
   */
  removeActionAndItsChildren(actionToDeleteId, firstActionParentEdgeId, removeAllChildren, childToParentEdges, branchIndexToKeep = DEFAULT_BRANCH_INDEX) {
    let newActions = this.getActions();
    const actionToDelete = this.getActionById(actionToDeleteId);
    const actionToDeleteConnection = actionToDelete.getConnection();
    if (removeAllChildren) {
      newActions = this.removeActionAndAllDownstreamActions({
        firstActionId: actionToDeleteId,
        firstActionParentEdgeId,
        initialChildToParentEdges: childToParentEdges,
        actions: newActions
      });
    } else if (actionToDelete.isBranch() && actionToDeleteConnection) {
      actionToDeleteConnection.getConnectors().map((connector, index) => ({
        id: connector && connector.getNextActionId(),
        edgeId: `${actionToDeleteId}-edge-${index + 1}`
      })).filter((_, index) => index - 1 !== branchIndexToKeep).filter(actionMeta => actionMeta.id).forEach(rootActionToDelete => {
        newActions = this.removeActionAndAllDownstreamActions({
          firstActionId: rootActionToDelete.id,
          firstActionParentEdgeId: rootActionToDelete.edgeId,
          initialChildToParentEdges: childToParentEdges,
          actions: newActions
        });
      });
      // also delete the root
      newActions = newActions.remove(actionToDeleteId);
    } else {
      newActions = newActions.remove(actionToDeleteId);
    }
    return newActions;
  }

  /**
   * Starting at `firstActionId`, recursively delete all
   * actions downstream. Stops at GOTO actions.
   *
   * @return the new `actions` instance with removed actions.
   */
  removeActionAndAllDownstreamActions({
    firstActionId,
    actions,
    firstActionParentEdgeId,
    initialChildToParentEdges
  }) {
    let newActions = actions;
    const childToParentEdges = JSON.parse(JSON.stringify(initialChildToParentEdges));
    function recurseFunction(actionId, previousEdgeId) {
      const action = newActions.get(actionId);
      if (!action) {
        return newActions;
      }
      const actionConnection = action.getConnection();
      const previousEdges = childToParentEdges[actionId] || [];
      const previousEdgeIndex = previousEdges.findIndex(edge => edge.edgeId === previousEdgeId);
      if (previousEdges[previousEdgeIndex] && previousEdges[previousEdgeIndex].edgeType === ConnectorEdgeTypes.GOTO) {
        childToParentEdges[actionId].splice(previousEdgeIndex);
        return newActions;
      }

      // iterate through the parents' edges and for the ones that are
      if (childToParentEdges[actionId]) {
        childToParentEdges[actionId].forEach(parentEdge => {
          if (parentEdge.edgeId === previousEdgeId) {
            return;
          }
          const previousActionId = getPreviousActionId(parentEdge.edgeId);
          const previousAction = newActions.get(previousActionId);
          if (!previousAction) {
            return;
          }
          const newAction = previousAction.updateEdgeInConnection(parentEdge.edgeId, null, false);
          newActions = newActions.set(previousActionId, newAction);
        });
        delete childToParentEdges[actionId];
      }

      // termination condition: no connection
      if (!actionConnection) {
        newActions = newActions.remove(actionId);
        return newActions;
      }
      actionConnection.getConnectors().forEach((connector, index) => {
        if (connector && !connector.isGoToEdge()) {
          newActions = recurseFunction(connector.getNextActionId(), `${actionId}-edge-${index + 1}`);
        }
      });
      newActions = newActions.remove(actionId);
      return newActions;
    }
    return recurseFunction(firstActionId, firstActionParentEdgeId);
  }
  findAllAncestorsAndDecendantsOfAction(actionId, isNewAction) {
    return findAllAncestorsAndDecendantsOfAction(this, actionId, isNewAction);
  }
}